import React, { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  TextField,
  Button,
  Typography,
  Box,
  Alert,
  CircularProgress,
  IconButton,
  InputAdornment,
  Tooltip,
  useTheme,
  useMediaQuery,
  Collapse,
} from '@mui/material';
import { Fade } from '@mui/material';
import { Microsoft, VisibilityOutlined as Visibility, VisibilityOffOutlined as VisibilityOff } from '@mui/icons-material';
import backendUrl from '../store/config';
import { useNavigate } from 'react-router-dom';
import { useAppSelector, useAppDispatch } from '../store/hooks';
import { login, clearError, clearFieldErrors, clearSuccess } from '../store/slices/authSlice';
import LightThemeWrapper from '../components/LightThemeWrapper';
import PublicNavbar from '../components/PublicNavbar';

const Login: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showEmailLogin, setShowEmailLogin] = useState(false);
  const [sessionExpiredMessage, setSessionExpiredMessage] = useState<string | null>(null);
  const [touched, setTouched] = useState({
    email: false,
    password: false
  });
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // Explicitly type the auth state to avoid 'unknown' property errors
  interface ValidationErrors {
    [field: string]: string[];
  }

  interface AuthState {
    loading: boolean;
    error: string | null;
    fieldErrors: ValidationErrors | null;
    successMessage: string | null;
    isAuthenticated: boolean;
  }

  const { loading, error, fieldErrors, successMessage, isAuthenticated } = useAppSelector(
    (state: { auth: AuthState }) => state.auth
  );

  useEffect(() => {
    // Only redirect if authenticated and not loading (to avoid flash)
    if (isAuthenticated && !loading) {
      if (successMessage) {
        // Show success message for 2 seconds before redirecting
        const timer = setTimeout(() => {
          navigate('/getting-started');
        }, 2000);
        return () => clearTimeout(timer);
      } else {
        // Already authenticated, redirect immediately
        navigate('/getting-started');
      }
    }
  }, [isAuthenticated, loading, successMessage, navigate]);

  useEffect(() => {
    // Check for session expiration message
    const expiredMessage = sessionStorage.getItem('sessionExpiredMessage');
    if (expiredMessage) {
      setSessionExpiredMessage(expiredMessage);
      sessionStorage.removeItem('sessionExpiredMessage');
    }
    
    return () => {
      dispatch(clearError());
      dispatch(clearSuccess());
    };
  }, [dispatch]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Mark all fields as touched
    setTouched({
      email: true,
      password: true
    });
    
    // Clear any existing field errors before submitting
    dispatch(clearFieldErrors());
    dispatch(login({ email, password }));
  };

  // Handle field blur events to show validation errors
  const handleFieldBlur = (field: string) => {
    setTouched(prev => ({ ...prev, [field]: true }));
  };

  const handleMicrosoftLogin = () => {
    window.location.href = `${backendUrl}/api/auth/microsoft-login`;
  };

  return (
    <LightThemeWrapper>
      <PublicNavbar />
      <Box sx={{ 
        minHeight: '100vh', 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center',
        pt: isMobile ? 4 : 8,
        px: isMobile ? 2 : 0
      }}>
        <Fade in timeout={600}>
          <Container maxWidth="xs">
      <Paper elevation={1} sx={{ 
        p: isMobile ? 2 : 3, 
        maxWidth: isMobile ? '100%' : 400, 
        mx: 'auto',
        borderRadius: isMobile ? 2 : 3
      }}>
        <Box sx={{ textAlign: 'center', mb: isMobile ? 2 : 3 }}>
          <Typography 
            variant={isMobile ? "h6" : "h5"} 
            component="h1" 
            gutterBottom 
            sx={{ 
              fontWeight: 700, 
              mb: 1, 
              color: '#000',
              fontSize: isMobile ? '1.25rem' : undefined
            }}
          >
            Welcome Back
          </Typography>
          <Typography 
            variant="body2" 
            color="text.secondary" 
            sx={{ fontSize: isMobile ? '0.8rem' : '0.875rem' }}
          >
            Sign in to your AGRANA account
          </Typography>
        </Box>

        {sessionExpiredMessage && (
          <Alert severity="warning" sx={{ mb: 2 }} onClose={() => setSessionExpiredMessage(null)}>
            {sessionExpiredMessage}
          </Alert>
        )}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        {successMessage && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {successMessage}
          </Alert>
        )}

        {/* Microsoft SSO Button */}
        <Button
          fullWidth
          variant="outlined"
          size={isMobile ? "small" : "medium"}
          startIcon={<Microsoft />}
          onClick={handleMicrosoftLogin}
          disabled={loading}
          sx={{
            mb: isMobile ? 2 : 2.5,
            py: isMobile ? 1 : 1.2,
            borderColor: '#a31830',
            color: '#a31830',
            fontSize: isMobile ? '0.8rem' : '0.875rem',
            '&:hover': {
              borderColor: '#8b1426',
              backgroundColor: 'rgba(163, 24, 48, 0.04)',
            },
          }}
        >
          Continue with Microsoft
        </Button>

        {/* Email Login Toggle Button */}
        <Box sx={{ textAlign: 'center', my: isMobile ? 2 : 2.5 }}>
          <Button
            onClick={() => setShowEmailLogin(!showEmailLogin)}
            size="small"
            sx={{
              color: 'text.secondary',
              textTransform: 'none',
              fontSize: isMobile ? '0.75rem' : '0.8rem',
              '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.04)',
              },
            }}
          >
            {showEmailLogin ? 'Hide email login' : 'Or sign in with email'}
          </Button>
        </Box>

        {/* Collapsible Email Login Form */}
        <Collapse in={showEmailLogin}>
          <Box>
            <Box component="form" onSubmit={handleSubmit} sx={{ mt: 1 }}>
              <TextField
                margin="normal"
                required
                fullWidth
                id="email"
                label="Email Address"
                name="email"
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                onBlur={() => handleFieldBlur('email')}
                size="small"
                error={!!(fieldErrors?.email && touched.email)}
                helperText={(fieldErrors?.email && touched.email) ? fieldErrors.email[0] : ''}
                sx={{ mb: isMobile ? 1 : 1.5 }}
              />
              <TextField
                margin="normal"
                required
                fullWidth
                name="password"
                label="Password"
                type={showPassword ? 'text' : 'password'}
                id="password"
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                onBlur={() => handleFieldBlur('password')}
                size="small"
                error={!!(fieldErrors?.password && touched.password)}
                helperText={(fieldErrors?.password && touched.password) ? fieldErrors.password[0] : ''}
                sx={{ mb: isMobile ? 2 : 2.5 }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <Tooltip title={showPassword ? "Hide password" : "Show password"}>
                        <IconButton
                          aria-label="toggle password visibility"
                          onClick={() => setShowPassword((prev) => !prev)}
                          edge="end"
                          size="small"
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </Tooltip>
                    </InputAdornment>
                  ),
                }}
              />
              <Button
                type="submit"
                fullWidth
                variant="contained"
                size={isMobile ? "small" : "medium"}
                disabled={loading}
                sx={{
                  mt: isMobile ? 1.5 : 2,
                  mb: isMobile ? 1.5 : 2,
                  py: isMobile ? 1 : 1.2,
                  fontSize: isMobile ? '0.8rem' : '0.875rem',
                  backgroundColor: '#a31830',
                  '&:hover': {
                    backgroundColor: '#8b1426',
                  },
                }}
              >
                {loading ? (
                  <CircularProgress size={20} color="inherit" />
                ) : (
                  'Sign In'
                )}
              </Button>
            </Box>

          </Box>
        </Collapse>
        </Paper>
          </Container>
        </Fade>
      </Box>
    </LightThemeWrapper>
  );
};

export default Login;